import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { ReportCourse } from '@/hooks/queries/useReportCourses';
import { useTabLabels } from '@/hooks/useTabLabels';

import CourseLink from './CourseLink';
import ReportTable, { type ReportColumn } from './ReportTable';
import { buildMaterialsRows, downloadCsv, reportCsvFilename } from './reportUtils';

type MaterialsReportProps = {
	courses: ReportCourse[];
	schoolId: string;
};

const COLUMNS: ReportColumn[] = [
	{ id: 'grade', label: 'Grade', sortType: 'grade' },
	{ id: 'discipline', label: 'Discipline' },
	{ id: 'course', label: 'Course' },
	{ id: 'resourceTitle', label: 'Resource Title' },
	{ id: 'resourceType', label: 'Type' },
	{ id: 'author', label: 'Author' },
	{ id: 'isbnOrDetail', label: 'ISBN/Detail' },
];

const MaterialsReport = ({ courses, schoolId }: MaterialsReportProps) => {
	const labels = useTabLabels();
	const resourcesLabel = labels.resources;
	const rows = buildMaterialsRows(courses);

	const handleDownload = () => {
		downloadCsv(
			reportCsvFilename('materials', schoolId),
			['Grade', 'Discipline', 'Course', 'Resource Title', 'Type', 'Author', 'ISBN/Detail'],
			rows.map((row) => [
				row.grade,
				row.discipline,
				row.course,
				row.resourceTitle,
				row.resourceType,
				row.author,
				row.isbnOrDetail,
			])
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-[16px] font-bold leading-5 text-(--header-text)">
						{resourcesLabel} by discipline × grade
					</h2>
					<p className="mt-1 text-sm text-(--header-text)">
						{rows.length} {resourcesLabel.toLowerCase()}
					</p>
				</div>
				<Button type="button" variant="outline" size="small" onClick={handleDownload}>
					<Download size={14} className="mr-1.5" />
					Download CSV
				</Button>
			</div>
			<ReportTable
				columns={COLUMNS}
				rows={rows.map((row) => ({
					key: row.courseResourcesId || `${row.courseId}|${row.resourceId}`,
					searchText: row.searchText,
					sortValues: [
						row.grade,
						row.discipline,
						row.course,
						row.resourceTitle,
						row.resourceType,
						row.author,
						row.isbnOrDetail,
					],
					cells: [
						row.grade || '—',
						row.discipline || '—',
						<CourseLink key="course" courseId={row.courseId}>
							{row.course}
						</CourseLink>,
						row.resourceTitle || '—',
						row.resourceType || '—',
						row.author || '—',
						row.isbnOrDetail || '—',
					],
				}))}
				emptyMessage={`No ${resourcesLabel.toLowerCase()} found for the current filters.`}
				searchPlaceholder={`Search ${resourcesLabel.toLowerCase()}…`}
			/>
		</div>
	);
};

export default MaterialsReport;
